import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import { matchPath, useLocation } from "react-router-dom";

const TITLE_PREFIX = "MR";
const DEFAULT_TITLE = TITLE_PREFIX;

const PageTitleContext = createContext({
  setPageTitle: () => {},
});

const formatDocumentTitle = (label) => {
  const trimmed = label?.toString().trim();
  return trimmed ? `${TITLE_PREFIX} | ${trimmed}` : DEFAULT_TITLE;
};

const resolveRouteTitle = (pathname, routes = []) => {
  const rankedRoutes = [...routes].sort((a, b) => {
    const aSegments = (a.path || "").split("/").filter(Boolean).length;
    const bSegments = (b.path || "").split("/").filter(Boolean).length;
    if (bSegments !== aSegments) return bSegments - aSegments;
    return (b.path || "").length - (a.path || "").length;
  });

  for (const route of rankedRoutes) {
    if (!route?.path) continue;

    const match = matchPath({ path: route.path, end: true }, pathname);
    if (match) {
      return route.title?.toString().trim() || null;
    }
  }

  return null;
};

export const PageTitleProvider = ({ children, routes = [] }) => {
  const location = useLocation();
  const [overrideTitle, setOverrideTitle] = useState(null);

  const routeTitle = useMemo(
    () => resolveRouteTitle(location.pathname, routes),
    [location.pathname, routes]
  );

  useEffect(() => {
    document.title = formatDocumentTitle(overrideTitle || routeTitle);
  }, [overrideTitle, routeTitle]);

  const setPageTitle = useCallback((title) => {
    const trimmed = title?.toString().trim();
    setOverrideTitle(trimmed || null);
  }, []);

  const value = useMemo(() => ({ setPageTitle }), [setPageTitle]);

  return (
    <PageTitleContext.Provider value={value}>
      {children}
    </PageTitleContext.Provider>
  );
};

PageTitleProvider.propTypes = {
  children: PropTypes.node.isRequired,
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      title: PropTypes.string,
    })
  ),
};

/**
 * Sets the browser tab title as `MR | {title}`.
 * Pass a string to override the route title (e.g. candidate name).
 * Pass null/undefined to keep the route-based title.
 */
export const usePageTitle = (title) => {
  const { setPageTitle } = useContext(PageTitleContext);

  useEffect(() => {
    if (title == null || title === "") {
      return undefined;
    }

    setPageTitle(title);
    return () => setPageTitle(null);
  }, [title, setPageTitle]);
};

export default PageTitleContext;
